import { ed25519 } from '@noble/curves/ed25519';
import { secp256k1 } from '@noble/curves/secp256k1';
import type { DerivedKeys } from './derive';

export interface RegistrationProof {
  /** ed25519 signature (64B) over the raw challenge — proves cere + solana. */
  identitySig: Uint8Array;
  /** secp256k1 uncompressed pubkey (65B, 0x04 || X || Y). */
  evmPubkey: Uint8Array;
  /** secp256k1 compact r||s signature (64B) over the raw challenge. */
  evmSig: Uint8Array;
}

/**
 * Sign the single-use registration challenge with the PRF-derived keys to
 * prove possession of the chain identity. The server (wallet-api)
 * verifies these against the claimed addresses. Spec §3.3 (revised).
 *
 * - ed25519: sign the raw 32-byte challenge (EdDSA hashes internally).
 * - secp256k1: ECDSA over the 32-byte challenge treated as the message digest
 *   (matches the server's `ecdsa.Verify(challenge, pubkey)`). The challenge is
 *   32 random single-use bytes, so it is passed directly as the prehashed
 *   digest — NOT re-hashed (no `{ prehash: true }`). low-S is enforced by
 *   @noble's default, so the compact form is canonical.
 */
export function signRegistrationProof(keys: DerivedKeys, challenge: Uint8Array): RegistrationProof {
  const identitySig = ed25519.sign(challenge, keys.edSeed);
  const evmSig = secp256k1.sign(challenge, keys.secpKey).toCompactRawBytes();
  return { identitySig, evmPubkey: keys.secpPubkeyUncompressed, evmSig };
}
